import { InvalidateQueryFilters, useQueryClient } from '@tanstack/react-query';
import { Invoice_Read } from '@treadinc/horizon-api-spec';
import { debounce } from 'lodash-es';
import { useEffect, useId, useRef } from 'react';

import { realTimeChannels } from '~services/realTimeChannels';
import {
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';

const INVOICE_UPDATE_BURST_WAIT_MS = 500;

export default function useInvalidateQueriesOnInvoiceUpdate(
  companyId: string | null | undefined,
  jobId: string,
  queryFilters: InvalidateQueryFilters[],
) {
  const queryClient = useQueryClient();
  const uniqueKey = useId();
  const queryFiltersRef = useRef(queryFilters);
  queryFiltersRef.current = queryFilters;

  useEffect(() => {
    if (!companyId) {
      return undefined;
    }

    const invalidateMatchingQueries = debounce(() => {
      queryFiltersRef.current.forEach((filters) => {
        void queryClient.invalidateQueries(filters);
      });
    }, INVOICE_UPDATE_BURST_WAIT_MS);

    const handler = ({ data }: { data: Invoice_Read }) => {
      if (data.job?.id === jobId) {
        invalidateMatchingQueries();
      }
    };

    subscribeWithMultipleHandlers(
      realTimeChannels.InvoiceUpdateChannel,
      companyId,
      uniqueKey,
      handler,
    );

    return () => {
      invalidateMatchingQueries.cancel();
      unsubscribeHandler(realTimeChannels.InvoiceUpdateChannel, companyId, uniqueKey);
    };
  }, [companyId, jobId, queryClient, uniqueKey]);
}
